import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  RefreshCw,
  Loader2,
  Mail,
  MoreHorizontal,
  Eye,
  X,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
} from "lucide-react";
import { format } from "date-fns";
import { navigate } from "wouter/use-browser-location";
import {
  useClassifyEmails,
  useFetchGmailEmails,
  useGmailEmails,
  useGmailSettings,
} from "@/hooks/useGmailEmails";
import { useAutoCreateQuotation } from "@/hooks/useAutoCreateQuotation";
import { useGmailConnectionStatus } from "@/hooks/useGmailAuth";
import { useEmailInboxStore } from "@/store/emailInboxStore";
import type {
  ClassifyEmailResult,
  EmailFilterMode,
  EmailQuotation,
  EmailQuotationStatus,
  ExtractedData,
  InMemoryEmail,
  ReviewRequiredReason,
} from "@/types/gmail";

// ==================== Status config ====================

const STATUS_CONFIG: Record<
  EmailQuotationStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: React.ReactNode;
  }
> = {
  PENDING_CLASSIFICATION: {
    label: "Pending AI",
    variant: "secondary",
    icon: <Clock className="h-3 w-3" />,
  },
  PENDING_REVIEW: {
    label: "Review Required",
    variant: "outline",
    icon: <AlertCircle className="h-3 w-3" />,
  },
  CONVERTED: {
    label: "Converted",
    variant: "default",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  REJECTED: {
    label: "Rejected",
    variant: "destructive",
    icon: <X className="h-3 w-3" />,
  },
  AUTO_REPLIED: {
    label: "Auto Replied",
    variant: "secondary",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
};

// ==================== Helpers ====================

/**
 * Convert an in-memory email into a shape compatible with EmailQuotation
 * for unified display. Uses messageId as the temporary id.
 */
function toDisplayEmail(
  email: InMemoryEmail,
): EmailQuotation & { _isInMemory: true } {
  return {
    id: email.messageId,
    businessId: "",
    branchId: "",
    gmailMessageId: email.messageId,
    gmailThreadId: email.threadId,
    fetchedByUserId: "",
    emailSubject: email.subject,
    emailFrom: email.fromEmail,
    emailDate: email.emailDate,
    emailSnippet: email.snippet || null,
    rawText: email.rawText,
    isQuotationRequest: null,
    classificationConfidence: null,
    classificationReason: null,
    extractedData: null,
    status: "PENDING_CLASSIFICATION",
    reviewNotes: null,
    reviewRequiredReason: null,
    quotationId: null,
    processedBy: null,
    processedAt: null,
    createdAt: email.emailDate,
    updatedAt: email.emailDate,
    _isInMemory: true,
  };
}

/**
 * Build a display record for a classified email result.
 * Stays visible for the rest of the session regardless of status.
 * - REJECTED emails are not saved to DB (_sessionOnly: true — no navigation, no dropdown).
 * - PENDING_REVIEW / CONVERTED emails have a savedId and are in DB (_sessionOnly: false — navigable).
 */
function toClassifiedDisplayEmail(
  email: InMemoryEmail,
  result: ClassifyEmailResult,
  overrideStatus?: EmailQuotationStatus,
): EmailQuotation & { _sessionOnly: boolean } {
  const status = overrideStatus ?? result.status;
  return {
    id: result.savedId || email.messageId,
    businessId: "",
    branchId: "",
    gmailMessageId: email.messageId,
    gmailThreadId: email.threadId,
    fetchedByUserId: "",
    emailSubject: email.subject,
    emailFrom: email.fromEmail,
    emailDate: email.emailDate,
    emailSnippet: email.snippet || null,
    rawText: email.rawText,
    isQuotationRequest: result.isQuotationRequest,
    classificationConfidence: result.confidence,
    classificationReason: result.reason,
    extractedData: result.extractedData as ExtractedData | null,
    status,
    reviewNotes: null,
    reviewRequiredReason: result.reviewRequiredReason as ReviewRequiredReason,
    quotationId: null,
    processedBy: null,
    processedAt: null,
    createdAt: email.emailDate,
    updatedAt: email.emailDate,
    // REJECTED emails are never saved to DB — block navigation and dropdown
    _sessionOnly: result.status === "REJECTED",
  };
}

// ==================== Component ====================

export const EmailInbox: React.FC = () => {
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [searchFilter, setSearchFilter] = React.useState("");
  const [emailFilter, setEmailFilter] = React.useState<EmailFilterMode>("all");
  const today = React.useMemo(() => new Date(), []);
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: today, to: today });
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [fetchPopoverOpen, setFetchPopoverOpen] = React.useState(false);

  // Session email state
  const {
    inMemoryEmails,
    existingFetchedEmails,
    classifiedSessionEmails,
    setInMemoryEmails,
    filterInMemoryEmails,
    setExistingFetchedEmails,
    updateExistingFetchedEmails,
    setClassifiedSessionEmails,
    appendClassifiedSessionEmails,
    updateClassifiedSessionEmails,
  } = useEmailInboxStore();

  const { data: connectionStatus } = useGmailConnectionStatus();
  const { data: gmailSettings } = useGmailSettings();
  const autoCreateEnabled = gmailSettings?.autoCreateEnabled ?? false;

  const fetchEmails = useFetchGmailEmails();
  const classifyEmails = useClassifyEmails();
  const { autoCreateBatch } = useAutoCreateQuotation();
  const [isProcessingPending, setIsProcessingPending] = React.useState(false);

  // Load PENDING_REVIEW emails from DB so the user can auto-create them after switching modes
  const { data: pendingReviewData } = useGmailEmails({
    status: "PENDING_REVIEW",
    limit: 100,
  });
  const pendingReviewFromDB: EmailQuotation[] = (
    pendingReviewData?.data ?? []
  ).filter(
    (e) =>
      e.status === "PENDING_REVIEW" &&
      e.extractedData !== null &&
      // Don't show if already processed this session
      !classifiedSessionEmails.some((s) => s.id === e.id),
  );

  // Combine already-processed emails found during last fetch + classified this session + new in-memory emails
  const inMemoryDisplayEmails = inMemoryEmails.map(toDisplayEmail);
  const allEmails: Array<
    EmailQuotation & { _isInMemory?: boolean; _sessionOnly?: boolean }
  > = [
    ...existingFetchedEmails,
    ...classifiedSessionEmails,
    ...inMemoryDisplayEmails,
  ];

  // Client-side filtering
  const filteredEmails = allEmails.filter((email) => {
    if (statusFilter !== "all" && email.status !== statusFilter) return false;
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      return (
        email.emailSubject.toLowerCase().includes(search) ||
        email.emailFrom.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // ==================== Handlers ====================

  const handleFetch = () => {
    fetchEmails.mutate(
      {
        dateFrom: dateRange.from
          ? format(dateRange.from, "yyyy-MM-dd")
          : undefined,
        dateTo: dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : undefined,
        filter: emailFilter,
      },
      {
        onSuccess: (result) => {
          // Replace in-memory emails with the newly fetched ones
          setInMemoryEmails(result.newEmails);
          // Show already-processed emails found in this fetch (shown with their real status)
          setExistingFetchedEmails(result.existingEmails || []);
          // Clear session-classified emails — DB emails above now take over for PENDING_REVIEW/CONVERTED
          setClassifiedSessionEmails([]);
          setSelectedIds(new Set());
          setFetchPopoverOpen(false);
        },
      },
    );
  };

  const handleClassifySelected = () => {
    // Only classify in-memory emails (DB emails are already classified)
    const emailsToClassify = inMemoryEmails.filter((e) =>
      selectedIds.has(e.messageId),
    );
    if (emailsToClassify.length === 0) return;

    classifyEmails.mutate(emailsToClassify, {
      onSuccess: async (result) => {
        const classifiedIds = new Set(result.results.map((r) => r.messageId));
        const inMemoryMap = new Map(
          emailsToClassify.map((e) => [e.messageId, e]),
        );

        // Build session display records (all statuses stay visible until page refresh)
        const sessionEntries = result.results.map((r) =>
          toClassifiedDisplayEmail(inMemoryMap.get(r.messageId)!, r),
        );

        filterInMemoryEmails((e) => !classifiedIds.has(e.messageId));
        appendClassifiedSessionEmails(sessionEntries);
        setSelectedIds(new Set());

        // Auto-create quotations if mode is enabled
        if (autoCreateEnabled) {
          const autoResults = await autoCreateBatch(result.results);
          if (autoResults) {
            // Update successfully auto-created emails to CONVERTED in the session list
            const convertedIds = new Set(
              autoResults.filter((r) => r.success).map((r) => r.emailMessageId),
            );
            updateClassifiedSessionEmails((prev) =>
              prev.map((e) =>
                convertedIds.has(e.gmailMessageId)
                  ? { ...e, status: "CONVERTED" as const }
                  : e,
              ),
            );
          }
        }
      },
    });
  };

  const handleClassifyAll = () => {
    if (inMemoryEmails.length === 0) return;

    const emailsToClassify = [...inMemoryEmails];
    classifyEmails.mutate(emailsToClassify, {
      onSuccess: async (result) => {
        const inMemoryMap = new Map(
          emailsToClassify.map((e) => [e.messageId, e]),
        );

        // Build session display records (all statuses stay visible until page refresh)
        const sessionEntries = result.results.map((r) =>
          toClassifiedDisplayEmail(inMemoryMap.get(r.messageId)!, r),
        );

        setInMemoryEmails([]);
        appendClassifiedSessionEmails(sessionEntries);
        setSelectedIds(new Set());

        if (autoCreateEnabled) {
          const autoResults = await autoCreateBatch(result.results);
          if (autoResults) {
            // Update successfully auto-created emails to CONVERTED in the session list
            const convertedIds = new Set(
              autoResults.filter((r) => r.success).map((r) => r.emailMessageId),
            );
            updateClassifiedSessionEmails((prev) =>
              prev.map((e) =>
                convertedIds.has(e.gmailMessageId)
                  ? { ...e, status: "CONVERTED" as const }
                  : e,
              ),
            );
          }
        }
      },
    });
  };

  const toggleSelect = (id: string) => {
    // Only allow selecting in-memory emails (for classification)
    const isInMemory = inMemoryEmails.some((e) => e.messageId === id);
    if (!isInMemory) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Process all PENDING_REVIEW emails from the DB using auto-create.
   * Used when the user switches to Auto Create mode and wants to retroactively
   * process emails that were previously left in Review Required status.
   */
  const handleProcessPendingReview = async () => {
    if (pendingReviewFromDB.length === 0) return;
    setIsProcessingPending(true);
    try {
      // Convert EmailQuotation records to ClassifyEmailResult format for autoCreateBatch
      const classifyResults: ClassifyEmailResult[] = pendingReviewFromDB
        .filter((e) => e.extractedData !== null)
        .map((e) => ({
          messageId: e.gmailMessageId,
          status: "PENDING_REVIEW" as const,
          savedId: e.id,
          isQuotationRequest: true,
          extractedData: e.extractedData as unknown as Record<string, unknown>,
          confidence: e.classificationConfidence ?? 0,
          reason: e.classificationReason ?? "",
          reviewRequiredReason: e.reviewRequiredReason,
        }));

      const results = await autoCreateBatch(classifyResults);
      if (results) {
        const convertedIds = new Set(
          results.filter((r) => r.success).map((r) => r.emailMessageId),
        );
        // Reflect successful conversions in the session display
        const nowConverted = pendingReviewFromDB
          .filter((e) => convertedIds.has(e.gmailMessageId))
          .map((e) => ({
            ...e,
            status: "CONVERTED" as const,
            _sessionOnly: false,
          }));
        if (nowConverted.length > 0) {
          updateExistingFetchedEmails((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            return [
              ...prev.map((e) =>
                convertedIds.has(e.gmailMessageId)
                  ? { ...e, status: "CONVERTED" as const }
                  : e,
              ),
              ...nowConverted.filter((e) => !existingIds.has(e.id)),
            ];
          });
        }
      }
    } finally {
      setIsProcessingPending(false);
    }
  };

  const pendingClassificationCount = inMemoryEmails.length;

  // ==================== Not Connected ====================

  if (!connectionStatus?.connected) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
        <Mail className="h-12 w-12 text-muted-foreground" />
        <div>
          <h2 className="text-lg font-semibold">Gmail Not Connected</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your Gmail account in Settings to start receiving quotation
            emails.
          </p>
        </div>
        <Button onClick={() => navigate("/gmail-settings")}>
          Go to Gmail Settings
        </Button>
      </div>
    );
  }

  // ==================== Render ====================

  return (
    <div className="space-y-4">
      {/* Banner: pending review emails that can be auto-created when mode is Active */}
      {autoCreateEnabled && pendingReviewFromDB.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <Zap className="h-4 w-4 shrink-0" />
            <span>
              <strong>{pendingReviewFromDB.length}</strong> email
              {pendingReviewFromDB.length !== 1 ? "s" : ""} in{" "}
              <strong>Review Required</strong> status can be auto-created now.
            </span>
          </div>
          <Button
            size="sm"
            className="gap-2 shrink-0"
            onClick={handleProcessPendingReview}
            disabled={isProcessingPending}
          >
            {isProcessingPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Zap className="h-4 w-4" />
            )}
            {isProcessingPending
              ? "Processing..."
              : `Auto-Create All (${pendingReviewFromDB.length})`}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Inbox</h1>
          <p className="text-sm text-muted-foreground">
            Gmail quotation emails fetched and processed by AI.
            {autoCreateEnabled && (
              <span className="ml-2 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Zap className="h-3 w-3" />
                Auto Create mode active
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingClassificationCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClassifyAll}
              disabled={classifyEmails.isPending}
              className="gap-2"
            >
              {classifyEmails.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : autoCreateEnabled ? (
                <Zap className="h-4 w-4" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {autoCreateEnabled
                ? `Classify & Auto-Create (${pendingClassificationCount})`
                : `Classify All (${pendingClassificationCount})`}
            </Button>
          )}

          {/* Fetch Emails popover */}
          <Popover open={fetchPopoverOpen} onOpenChange={setFetchPopoverOpen}>
            <PopoverTrigger asChild>
              <Button disabled={fetchEmails.isPending} className="gap-2">
                {fetchEmails.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Fetch Emails
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4 space-y-3" align="end">
              <p className="text-sm font-medium">Fetch Options</p>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Date range (optional)
                </p>
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range) =>
                    setDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
                {(dateRange.from || dateRange.to) && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-muted-foreground">
                      {dateRange.from && dateRange.to
                        ? `${format(dateRange.from, "MMM d")} – ${format(dateRange.to, "MMM d, yyyy")}`
                        : dateRange.from
                          ? format(dateRange.from, "MMM d, yyyy")
                          : ""}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setDateRange({ from: undefined, to: undefined })
                      }
                    >
                      <X className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Email filter</p>
                <Select
                  value={emailFilter}
                  onValueChange={(v) => setEmailFilter(v as EmailFilterMode)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Emails</SelectItem>
                    <SelectItem value="unread">Unread Only</SelectItem>
                    <SelectItem value="read">Read Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full gap-2"
                onClick={handleFetch}
                disabled={fetchEmails.isPending}
              >
                {fetchEmails.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {fetchEmails.isPending ? "Fetching..." : "Fetch"}
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search subject or sender..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-64"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING_CLASSIFICATION">
                  Pending AI
                </SelectItem>
                <SelectItem value="PENDING_REVIEW">Review Required</SelectItem>
                <SelectItem value="CONVERTED">Converted</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClassifySelected}
                  disabled={classifyEmails.isPending}
                  className="gap-2"
                >
                  {autoCreateEnabled ? (
                    <Zap className="h-4 w-4" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {autoCreateEnabled
                    ? `Classify & Auto-Create (${selectedIds.size})`
                    : `Classify Selected (${selectedIds.size})`}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Table */}
      <Card>
        <CardContent className="p-0">
          {fetchEmails.isPending ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center gap-2">
              <Mail className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No emails found. Click "Fetch Emails" to load from Gmail.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Subject</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>AI Result</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmails.map((email) => {
                  const isInMemory =
                    (email as { _isInMemory?: boolean })._isInMemory === true;
                  // REJECTED emails classified this session — in session display only, not in DB
                  const isSessionOnly =
                    (email as { _sessionOnly?: boolean })._sessionOnly === true;
                  const canNavigate = !isInMemory && !isSessionOnly;
                  const statusConfig =
                    STATUS_CONFIG[email.status] ||
                    STATUS_CONFIG.PENDING_CLASSIFICATION;

                  return (
                    <TableRow
                      key={email.id}
                      className={`${isInMemory ? "opacity-70" : ""} ${canNavigate ? "cursor-pointer" : "cursor-default"} hover:bg-muted/50`}
                      onClick={() => {
                        if (canNavigate)
                          navigate(`/email-quotation/${email.id}`);
                      }}
                    >
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        title={
                          isInMemory
                            ? "Select to classify"
                            : "Already classified — view details instead"
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(email.id)}
                          onChange={() => toggleSelect(email.id)}
                          disabled={!isInMemory}
                          className="h-4 w-4 disabled:opacity-30 disabled:cursor-not-allowed"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium truncate max-w-[280px]">
                          {email.emailSubject}
                        </div>
                        {email.emailSnippet && (
                          <div className="text-xs text-muted-foreground truncate max-w-[280px]">
                            {email.emailSnippet}
                          </div>
                        )}
                        {isInMemory && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                            Not classified yet — select and click Classify
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {email.emailFrom.replace(/<.*>/, "").trim() ||
                          email.emailFrom}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(email.emailDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell>
                        {email.isQuotationRequest === null ? (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        ) : email.isQuotationRequest ? (
                          <Badge
                            variant="outline"
                            className="text-green-600 border-green-300 gap-1"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Quotation
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-orange-500 border-orange-300 gap-1"
                          >
                            <X className="h-3 w-3" />
                            Not Quotation
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={statusConfig.variant}
                          className="gap-1 text-xs"
                        >
                          {statusConfig.icon}
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {canNavigate && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/email-quotation/${email.id}`)
                                }
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailInbox;
